import styled from '@emotion/styled'
import { White } from '@mehilainen/mds-customer/colors'
import { Gray600 } from '@mehilainen/mds-customer/colors'
import { Ban, MapMarker } from '@mehilainen/mds-customer/icons'
import { useAtomValue } from 'jotai'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  LocationAutocompleteResult,
  LocationResult,
  LocationType,
  PublicNode,
  SupportedLanguage,
} from '../../../__generated__/api'
import { isOHCAtom } from '../../../state/common/atoms'
import { useSelectedNodeIdOrDefault } from '../../../xstate/selectors'
import { useIsMobile } from '../../hooks/useBreakpoint'
import { useLocationSearch } from '../../hooks/useSearch'
import { getGeolocateResults } from '../../services/geolocate'
import ResponsiveModal from '../Modal/ResponsiveModal/ResponsiveModal'
import { LocationIcon } from '../Search/LocationIcon'
import Search from '../Search/Search'
import { SearchTreeItem } from '../Search/types'
import SelectInput from '../SelectInput/SelectInput'

import {
  convertGeolocateResultToSearchTreeItem,
  convertLocationsToSearchTreeItems,
  isAllGeolocateLocation,
  isGeolocateLocation,
} from './utils'

const LocationContainer = styled.div<{ isMobile?: boolean }>`
  padding: ${(props) => (props.isMobile ? '0 0 24px 0' : '32px 24px 32px 24px')};
  background: ${(props) => (props.isMobile ? White : 'transparent')};
`

interface Props {
  locations: LocationResult[]
  value: string[]
  selectedNode: PublicNode | null
  onSelect(location: string[]): void
}

const LocationSelect: React.FC<React.PropsWithChildren<Props>> = ({
  locations,
  value,
  selectedNode,
  onSelect,
}) => {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = useState<boolean>(false)
  const [breadcrumb, setBreadcrumb] = useState<Array<SearchTreeItem<LocationResult>>>([])
  const [searchPending, setSearchPending] = useState<boolean>(false)
  const isMobile = useIsMobile()
  const isOhc = useAtomValue(isOHCAtom)
  const nodeId = useSelectedNodeIdOrDefault()
  const locationSearch = useLocationSearch(isOhc, nodeId)

  const closeCallback = useCallback(() => {
    setBreadcrumb([])
    setOpen(false)
  }, [])

  const allData = useMemo(
    () => [
      convertGeolocateResultToSearchTreeItem(t),
      ...convertLocationsToSearchTreeItems(
        locations,
        t('component.locationSelect.rootSearchItemTitle'),
        t('component.locationSelect.rootSearchItemSubtitle')
      ),
    ],
    [locations, t]
  )
  const popularData = useMemo(() => allData.filter((i) => i.item?.favorite === true), [allData])

  const lastSelectedNameRef = useRef<string | undefined>()
  const selectedName = (() => {
    if (value.length !== 1) {
      return t('component.locationSelect.multipleSelected')
    }
    const found = locations.find((location) => value[0] === location.uniqueId)?.name
    if (found) {
      lastSelectedNameRef.current = found
    }
    return found ?? lastSelectedNameRef.current
  })()

  const onLocationSelect = (location: LocationResult) => {
    if (isGeolocateLocation(location)) {
      setSearchPending(true)
      getGeolocateResults(i18n.language as SupportedLanguage, selectedNode?.id, isOhc).then(
        (result) => {
          setSearchPending(false)
          setBreadcrumb((prev) => [...prev, convertGeolocateResultToSearchTreeItem(t, result)])
        }
      )
      return
    } else if (isAllGeolocateLocation(location)) {
      onSelect(location.clinicIDs)
    } else {
      lastSelectedNameRef.current = location.name
      onSelect([location.uniqueId])
    }
    closeCallback()
  }

  const mapDataToListProps = (item: SearchTreeItem<LocationResult>) => {
    const title = item.name
    const description = item.caption || item.subtitle || undefined

    const startIcon = <LocationIcon item={item} />
    const endIcon = item.children && item.children?.length > 0 ? undefined : false

    return { title, description, startIcon, endIcon }
  }

  const mapSearchResultsToListProps = (item: LocationAutocompleteResult) => {
    const title = item.name
    const description = undefined

    const locationSupported = item.supported !== false
    const startIcon =
      (!locationSupported && <Ban htmlColor={Gray600} />) ||
      (item.type !== LocationType.Clinic && <MapMarker />) ||
      (item && <LocationIcon item={item} />) ||
      undefined

    const endIcon = false

    return { title, description, startIcon, endIcon }
  }

  const groupSearchResultsBy = (item: LocationAutocompleteResult) =>
    item.supported !== false
      ? t('component.locationSelect.citiesAndLocations')
      : t('component.locationSelect.unsupportedLocationsTitle')

  return (
    <>
      <SelectInput
        label={t('component.locationSelect.label')}
        value={selectedName}
        onClick={() => setOpen(true)}
        dataCy="locationSelect-textField"
      />
      <ResponsiveModal
        open={open}
        onClose={closeCallback}
        fullWidth
        disableMobileClose
        omitDefaultPadding
        sx={{
          '& .MuiDialog-paper': {
            overflowY: 'visible',
          },
        }}
      >
        <LocationContainer isMobile={isMobile}>
          <Search
            searchFieldLabel={t('component.locationSelect.searchFieldLabel')}
            allData={allData}
            popularData={popularData}
            breadcrumb={breadcrumb}
            onBreadcrumbChange={(crumb) => {
              setBreadcrumb(crumb)
            }}
            searchPending={searchPending}
            onSelectAndClose={onLocationSelect}
            onClose={closeCallback}
            placeholder={t('component.locationSelect.placeholder')}
            searchIndex={locationSearch}
            getOptionLabel={(option) => option.name}
            popularHeading={t('component.locationSelect.popularHeading')}
            popularLabel={t('component.locationSelect.popularLabel')}
            allDataHeading={t('component.locationSelect.allDataHeading')}
            allDataLabel={t('component.locationSelect.allDataLabel')}
            optionListModifier={(options) =>
              options
                .map((option) => ({
                  ...option,
                  supported: option.supported !== false,
                }))
                .sort(
                  (a, b) => Number(b.supported) - Number(a.supported) || a.priority - b.priority
                )
            }
            open={open}
            mapDataToListProps={mapDataToListProps}
            mapSearchResultsToListProps={mapSearchResultsToListProps}
            groupSearchResultsBy={groupSearchResultsBy}
          />
        </LocationContainer>
      </ResponsiveModal>
    </>
  )
}

export default LocationSelect
